import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDebounce } from '../../hooks/useDebounce';
import api from '../../services/api';
import { NormalizeSkuInput, OnlyDigits, ValidateGtin, ValidateNewSku } from '../../utils/productCodes';
import {
    ApplyFieldChange,
    BlingSnapshotSuggestions,
    BuildCreatePayload,
    BuildUpdatePayload,
    BuildWmsAttributesPatch,
    DiffProductForm,
    EMPTY_PRODUCT_FORM,
    MapSaveError,
    PendingFieldMessages,
    PRODUCT_FORM_FIELDS,
    ProductFormFromDto,
    ValidateProductForm,
    WMS_ATTRIBUTE_FIELDS,
} from './productForm';

// Espera de digitação antes de consultar o servidor (SKU livre, GTIN repetido).
const REMOTE_CHECK_DEBOUNCE_MS = 400;
const GTIN_USAGE_LIMIT = 5;

function sortByFormOrder(fields) {
    return [...fields].sort((a, b) => PRODUCT_FORM_FIELDS.indexOf(a) - PRODUCT_FORM_FIELDS.indexOf(b));
}

function skuList(rows) {
    return rows.map((row) => row.sku_display || row.sku).join(', ');
}

/**
 * Onde mais um GTIN aparece (`GET /products?ean=` casa `ean`, `tax_ean` ou
 * `case_gtin`), sem o próprio produto. Só consulta quando `enabled` (campo
 * alterado e GTIN válido): o repetido que já estava gravado aparece pela
 * pendência `GTIN_REPETIDO` do servidor.
 */
function useGtinUsage(code, { enabled, excludeId }) {
    const debouncedCode = useDebounce(enabled ? code : '', REMOTE_CHECK_DEBOUNCE_MS);
    const [usage, setUsage] = useState({ code: '', rows: [] });

    useEffect(() => {
        if (!debouncedCode) {
            setUsage({ code: '', rows: [] });
            return undefined;
        }
        let cancelled = false;
        api.getCatalogProducts({ ean: debouncedCode, limit: GTIN_USAGE_LIMIT })
            .then((data) => {
                if (cancelled) {
                    return;
                }
                const rows = (Array.isArray(data?.results) ? data.results : []).filter((row) => row.id !== excludeId);
                setUsage({ code: debouncedCode, rows });
            })
            .catch(() => {
                if (!cancelled) {
                    setUsage({ code: '', rows: [] });
                }
            });
        return () => {
            cancelled = true;
        };
    }, [debouncedCode, excludeId]);

    return enabled && usage.code === code ? usage.rows : [];
}

function gtinUsageWarning(code, rows) {
    if (!code || rows.length === 0) {
        return null;
    }
    const asGtin = rows.filter((row) => row.ean === code || row.case_gtin === code);
    if (asGtin.length > 0) {
        return `Já usado como GTIN em ${skuList(asGtin)}: o servidor recusa GTIN repetido ao salvar`;
    }
    return `Também é o GTIN tributário de ${skuList(rows)}`;
}

/**
 * Estado do editor de produto (`/produtos/novo` e `/produtos/:productId`).
 *
 * Carga: no `edit`, detalhe (`GET /products/:id`) + marcas; no `new`, só as
 * marcas. `currentIdRef` descarta resposta de outro produto. O rascunho só
 * grava no `save()`: PUT com os campos alterados (ou POST no novo) e, à parte,
 * os atributos do WMS (`wms:manage:products`). Falha parcial (cadastro salvo,
 * WMS não) deixa só os atributos como alteração pendente.
 *
 * Erros exibidos: os do cliente aparecem no campo alterado ou depois da
 * primeira tentativa de salvar; os do servidor (400/409/422 com `details`)
 * vencem até o campo mudar.
 */
export function useProductEditor({ mode, productId = null, canEdit = false, canManageWms = false }) {
    const isNew = mode === 'new';
    const [status, setStatus] = useState(isNew ? 'ready' : 'loading');
    const [loadError, setLoadError] = useState(null);
    const [product, setProduct] = useState(null);
    const [initialForm, setInitialForm] = useState(() => ({ ...EMPTY_PRODUCT_FORM }));
    const [form, setForm] = useState(() => ({ ...EMPTY_PRODUCT_FORM }));
    const [serverErrors, setServerErrors] = useState({});
    const [formError, setFormError] = useState(null);
    const [submitAttempted, setSubmitAttempted] = useState(false);
    const [saving, setSaving] = useState(false);
    const [brands, setBrands] = useState([]);
    const [brandsLoading, setBrandsLoading] = useState(true);
    const [brandsError, setBrandsError] = useState(null);

    const currentIdRef = useRef(productId);
    currentIdRef.current = productId;

    const applyDto = useCallback((dto, { keepWmsFrom = null } = {}) => {
        const next = ProductFormFromDto(dto);
        setProduct(dto);
        setInitialForm(next);
        if (keepWmsFrom) {
            const pending = { ...next };
            for (const field of WMS_ATTRIBUTE_FIELDS) {
                pending[field] = keepWmsFrom[field];
            }
            setForm(pending);
        } else {
            setForm(next);
        }
        setServerErrors({});
        setSubmitAttempted(false);
    }, []);

    const loadBrands = useCallback(async () => {
        setBrandsLoading(true);
        try {
            const data = await api.getCatalogBrands();
            setBrands(Array.isArray(data?.results) ? data.results : []);
            setBrandsError(null);
            return true;
        } catch (error) {
            setBrandsError(error?.message || 'Lista de marcas indisponível');
            return false;
        } finally {
            setBrandsLoading(false);
        }
    }, []);

    const loadProduct = useCallback(async () => {
        if (isNew || !productId) {
            return null;
        }
        const requestedId = productId;
        setLoadError(null);
        try {
            const dto = await api.getCatalogProduct(requestedId);
            if (currentIdRef.current !== requestedId) {
                return null;
            }
            applyDto(dto);
            setStatus('ready');
            return dto;
        } catch (error) {
            if (currentIdRef.current !== requestedId) {
                return null;
            }
            if (error?.status === 404) {
                setStatus('not_found');
            } else {
                setLoadError(error?.message || 'Não foi possível carregar o produto');
                setStatus((previous) => (previous === 'ready' ? previous : 'error'));
            }
            return null;
        }
    }, [applyDto, isNew, productId]);

    useEffect(() => {
        loadBrands();
        loadProduct();
    }, [loadBrands, loadProduct]);

    const reload = useCallback(() => {
        if (!isNew) {
            setStatus('loading');
        }
        loadBrands();
        return loadProduct();
    }, [isNew, loadBrands, loadProduct]);

    // ── Derivados ────────────────────────────────────────────────────────────

    const dirtyFields = useMemo(() => DiffProductForm(initialForm, form), [initialForm, form]);
    const isDirty = dirtyFields.size > 0;

    const validation = useMemo(
        () => ValidateProductForm(form, { mode: isNew ? 'new' : 'edit', dirtyFields, brands }),
        [form, isNew, dirtyFields, brands],
    );

    // SKU novo: regra + disponibilidade no servidor (vivo, deletado, parecidos).
    const normalizedSku = NormalizeSkuInput(form.sku);
    const skuLocallyValid = isNew && normalizedSku !== '' && ValidateNewSku(normalizedSku).errors.length === 0;
    const debouncedSku = useDebounce(skuLocallyValid ? normalizedSku : '', REMOTE_CHECK_DEBOUNCE_MS);
    const [skuCheck, setSkuCheck] = useState(null);

    useEffect(() => {
        if (!isNew || !canEdit || !debouncedSku) {
            setSkuCheck(null);
            return undefined;
        }
        let cancelled = false;
        api.checkCatalogSku(debouncedSku)
            .then((result) => {
                if (!cancelled) {
                    setSkuCheck({ sku: debouncedSku, result });
                }
            })
            .catch(() => {
                // Sem resposta: não trava o cadastro — o POST confere de novo.
                if (!cancelled) {
                    setSkuCheck({ sku: debouncedSku, result: null });
                }
            });
        return () => {
            cancelled = true;
        };
    }, [isNew, canEdit, debouncedSku]);

    const skuFeedback = useMemo(() => {
        if (!skuCheck || skuCheck.sku !== normalizedSku) {
            return { error: null, warning: null, checking: skuLocallyValid && canEdit };
        }
        if (!skuCheck.result) {
            return { error: null, warning: null, checking: false };
        }
        const { result } = skuCheck;
        if (result.conflict) {
            const who = result.conflict.title ? ` (${result.conflict.title})` : '';
            return {
                error: result.conflict.deleted
                    ? `Este SKU já existiu e foi excluído${who}: não dá para reaproveitar`
                    : `Este SKU já existe${who}`,
                warning: null,
                checking: false,
            };
        }
        if (result.valid === false && Array.isArray(result.errors) && result.errors.length > 0) {
            return { error: result.errors[0].message, warning: null, checking: false };
        }
        const similar = Array.isArray(result.similar) ? result.similar : [];
        return {
            error: null,
            warning: similar.length > 0 ? `Parecido com ${skuList(similar)}: confira se não é o mesmo produto` : null,
            checking: false,
        };
    }, [skuCheck, normalizedSku, skuLocallyValid, canEdit]);

    // GTIN repetido (aviso; o servidor é quem recusa com 409).
    const eanDigits = OnlyDigits(form.ean);
    const caseDigits = OnlyDigits(form.case_gtin);
    const excludeId = product?.id ?? null;
    const eanUsage = useGtinUsage(eanDigits, {
        enabled: dirtyFields.has('ean') && eanDigits !== '' && ValidateGtin(eanDigits).ok,
        excludeId,
    });
    const caseUsage = useGtinUsage(caseDigits, {
        enabled: dirtyFields.has('case_gtin') && caseDigits !== '' && ValidateGtin(caseDigits).ok,
        excludeId,
    });

    const pendingMessages = useMemo(
        () => PendingFieldMessages(product?.pending, { dirtyFields, product }),
        [product, dirtyFields],
    );

    const errors = useMemo(() => {
        const out = {};
        for (const [field, message] of Object.entries(validation.errors)) {
            if (submitAttempted || dirtyFields.has(field)) {
                out[field] = message;
            }
        }
        if (skuFeedback.error && !out.sku) {
            out.sku = skuFeedback.error;
        }
        return { ...out, ...serverErrors };
    }, [validation.errors, submitAttempted, dirtyFields, skuFeedback.error, serverErrors]);

    const warnings = useMemo(() => {
        const out = { ...pendingMessages.warnings, ...validation.warnings };
        if (skuFeedback.warning) {
            out.sku = skuFeedback.warning;
        }
        const eanWarning = gtinUsageWarning(eanDigits, eanUsage);
        if (eanWarning) {
            out.ean = eanWarning;
        }
        const caseWarning = gtinUsageWarning(caseDigits, caseUsage);
        if (caseWarning) {
            out.case_gtin = caseWarning;
        }
        return out;
    }, [
        pendingMessages.warnings,
        validation.warnings,
        skuFeedback.warning,
        eanDigits,
        eanUsage,
        caseDigits,
        caseUsage,
    ]);

    const blingSuggestions = useMemo(
        () => BlingSnapshotSuggestions(product?.bling_snapshot, form, { brands }),
        [product, form, brands],
    );

    // ── Ações ────────────────────────────────────────────────────────────────

    const setField = useCallback((name, value) => {
        setForm((previous) => ApplyFieldChange(previous, name, value));
        setServerErrors((previous) => {
            if (!(name in previous) && !(name === 'tax_origin' && 'fci_number' in previous)) {
                return previous;
            }
            const next = { ...previous };
            delete next[name];
            if (name === 'tax_origin') {
                delete next.fci_number;
            }
            return next;
        });
    }, []);

    const discard = useCallback(() => {
        setForm(initialForm);
        setServerErrors({});
        setFormError(null);
        setSubmitAttempted(false);
    }, [initialForm]);

    /**
     * Grava o rascunho. Devolve `{ ok, created?, wmsError?, firstErrorField?,
     * errorCount?, noop? }` — a página decide toast, foco e navegação.
     */
    const save = useCallback(async () => {
        if (!canEdit || saving) {
            return { ok: false };
        }
        setSubmitAttempted(true);
        const blocking = { ...validation.errors };
        if (skuFeedback.error) {
            blocking.sku = skuFeedback.error;
        }
        const errorFields = sortByFormOrder(Object.keys(blocking));
        if (errorFields.length > 0) {
            return { ok: false, errorCount: errorFields.length, firstErrorField: errorFields[0] };
        }
        if (!isNew && !isDirty) {
            return { ok: true, noop: true };
        }

        setSaving(true);
        setFormError(null);
        setServerErrors({});
        try {
            if (isNew) {
                const created = await api.createCatalogProduct(BuildCreatePayload(form));
                let wmsError = null;
                const wmsPatch = canManageWms
                    ? BuildWmsAttributesPatch(form, new Set(WMS_ATTRIBUTE_FIELDS.filter((field) => form[field])))
                    : null;
                if (wmsPatch && created?.id) {
                    try {
                        await api.putWarehouseProductAttributes(created.id, wmsPatch);
                    } catch (error) {
                        wmsError = error?.message || 'erro ao gravar';
                    }
                }
                return { ok: true, created, wmsError };
            }

            const patch = BuildUpdatePayload(form, dirtyFields);
            const wmsPatch = canManageWms ? BuildWmsAttributesPatch(form, dirtyFields) : null;
            let saved = null;
            if (Object.keys(patch).length > 0) {
                saved = await api.updateCatalogProduct(product.id, patch);
            }
            let wmsError = null;
            if (wmsPatch) {
                try {
                    await api.putWarehouseProductAttributes(product.id, wmsPatch);
                } catch (error) {
                    wmsError = error?.message || 'erro ao gravar';
                }
            }
            // O detalhe traz os atributos do WMS e as pendências recalculadas;
            // sem ele, o que foi gravado (PUT + atributos aceitos) vira a base.
            const fresh = await api.getCatalogProduct(product.id).catch(() => null);
            const base = fresh || { ...(saved || product), ...(wmsPatch && !wmsError ? wmsPatch : {}) };
            applyDto(base, { keepWmsFrom: wmsError ? form : null });
            return { ok: true, wmsError, cadastroSaved: Object.keys(patch).length > 0 };
        } catch (error) {
            const mapped = MapSaveError(error);
            setServerErrors(mapped.fieldErrors);
            setFormError(mapped.formError);
            const fields = sortByFormOrder(Object.keys(mapped.fieldErrors));
            return {
                ok: false,
                serverError: true,
                errorCount: fields.length,
                firstErrorField: fields[0] ?? null,
                message: mapped.formError || error?.message || null,
            };
        } finally {
            setSaving(false);
        }
    }, [
        applyDto,
        canEdit,
        canManageWms,
        dirtyFields,
        form,
        isDirty,
        isNew,
        product,
        saving,
        skuFeedback.error,
        validation.errors,
    ]);

    return {
        status,
        loadError,
        reload,
        product,
        initialForm,
        form,
        setField,
        errors,
        warnings,
        hints: pendingMessages.hints,
        dirtyFields,
        isDirty,
        formError,
        save,
        saving,
        discard,
        brands,
        brandsLoading,
        brandsError,
        reloadBrands: loadBrands,
        blingSuggestions,
        skuChecking: skuFeedback.checking,
    };
}

export default useProductEditor;
